import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  EditOutlined as EditIcon,
  LinkOffOutlined as UnassignIcon,
  LinkOutlined as LinkedIcon,
  PersonOutlined as ManagerIcon,
  SearchOffOutlined as NotFoundIcon,
  WarehouseOutlined as FacilityIcon,
} from '@mui/icons-material';
import AssignFacilityManagerDialog from '../../components/facilities/AssignFacilityManagerDialog';
import FacilityFormDialog from '../../components/facilities/FacilityFormDialog';
import LoadingSkeleton from '../../components/LoadingSkeleton';
import PageHeader from '../../components/PageHeader';
import PaginationControl from '../../components/PaginationControl';
import { APP_SHAPE } from '../../constants/shape';
import FlowBuilder from '../../components/collection-flow/FlowBuilder';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { useAuthStore } from '../../stores/auth.store';
import { useFacilityStore } from '../../stores/facility.store';
import { useOrganizationStore } from '../../stores/organization.store';
import type { UserSchema } from '../../types/auth.type';
import type { FacilityInput } from '../../types/facility.type';
import { userHasAnyLegacyRole } from '../../utils/permissions.helpers';

const ORDERS_PAGE_SIZE = 20;

// The two ops-manager roles eligible to be a facility manager.
function isEligibleManager(user: UserSchema): boolean {
  return userHasAnyLegacyRole(user, ['recycler_ops_manager', 'truck_co_ops_manager']);
}

export default function FacilityDetails() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { facilityId } = useParams<{ facilityId: string }>();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const {
    selectedFacility,
    selectedFacilityNotFound,
    isLoading,
    isSaving,
    error,
    clearError,
    fetchFacility,
    updateFacility,
    incomingOrders,
    incomingOrdersTotal,
    incomingOrdersLoading,
    fetchIncomingOrders,
  } = useFacilityStore();
  const { getOrganizationUsers } = useAuthStore();
  const { canMutate } = useAuthPermissions();

  const [formOpen, setFormOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [managerId, setManagerId] = useState('');
  const [managers, setManagers] = useState<UserSchema[]>([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [ordersPage, setOrdersPage] = useState(1);

  const orgId = selectedOrganization?.id;
  // A mirror of another organization's facility is theirs to manage: name, location,
  // manager and offload flow are read-only here; the routing org can only archive it.
  const isMirror = Boolean(selectedFacility?.linked_facility_id);
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.facility.write`)) && !isMirror;

  useEffect(() => {
    if (orgId && facilityId) void fetchFacility(orgId, facilityId);
  }, [orgId, facilityId, fetchFacility]);

  useEffect(() => {
    if (!orgId || !facilityId) return;
    void fetchIncomingOrders(orgId, facilityId, { page: ordersPage, limit: ORDERS_PAGE_SIZE });
  }, [orgId, facilityId, ordersPage, fetchIncomingOrders]);

  if (!selectedOrganization) return <Alert severity="info">{t('facilities.selectOrganization')}</Alert>;

  if (isLoading && (!selectedFacility || selectedFacility.id !== facilityId)) {
    return <LoadingSkeleton variant="cards" filters={false} cardCount={3} />;
  }

  if (selectedFacilityNotFound) {
    return (
      <Box>
        <PageHeader title={t('facilities.details.pageTitle')} onBack={() => navigate('/transportation/facilities')} backLabel={t('facilities.details.backLabel')} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }} />
        <Paper variant="outlined" sx={{ p: { xs: 3, sm: 6 }, textAlign: 'center', borderRadius: `${APP_SHAPE.radiusLg}px` }}>
          <NotFoundIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
          <Typography variant="h5">{t('facilities.details.notFoundTitle')}</Typography>
          <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate('/transportation/facilities')}>{t('facilities.details.backLabel')}</Button>
        </Paper>
      </Box>
    );
  }

  if (!selectedFacility || selectedFacility.id !== facilityId) {
    return (
      <Box>
        <PageHeader title={t('facilities.details.pageTitle')} onBack={() => navigate('/transportation/facilities')} backLabel={t('facilities.details.backLabel')} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }} />
        <Alert severity="error" action={<Button color="inherit" onClick={() => facilityId && void fetchFacility(selectedOrganization.id, facilityId)}>{t('suppliers.details.retry')}</Button>}>
          {error || t('facilities.details.loadError')}
        </Alert>
      </Box>
    );
  }

  const facility = selectedFacility;

  const openEdit = () => {
    clearError();
    setFormOpen(true);
  };

  const submitEdit = async (input: FacilityInput) => {
    const result = await updateFacility(selectedOrganization.id, facility.id, input);
    return Boolean(result);
  };

  const openAssign = () => {
    clearError();
    setManagerId('');
    setManagersLoading(true);
    void getOrganizationUsers(selectedOrganization.id)
      .then((users) => setManagers(users.filter(isEligibleManager)))
      .finally(() => setManagersLoading(false));
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    if (!managerId) return;
    const result = await updateFacility(selectedOrganization.id, facility.id, { manager_user_id: managerId });
    if (result) setAssignOpen(false);
  };

  const removeManager = () => void updateFacility(selectedOrganization.id, facility.id, { manager_user_id: null });

  return (
    <Box>
      <PageHeader
        icon={<FacilityIcon />}
        title={(
          <Stack direction="row" spacing={1.5} alignItems="center">
            <span>{facility.name}</span>
            <Chip size="small" label={t(`facilities.type.${facility.type}`)} />
            {facility.linked_owner_org ? (
              <Chip
                size="small"
                variant="outlined"
                icon={<LinkedIcon fontSize="small" />}
                label={t('facilities.linked.chip', { org: facility.linked_owner_org.name })}
              />
            ) : null}
            <Chip
              size="small"
              label={t(`facilities.status.${facility.status}`)}
              color={facility.status === 'active' ? 'success' : 'default'}
              variant={facility.status === 'active' ? 'filled' : 'outlined'}
            />
          </Stack>
        )}
        subtitle={facility.city ? t('facilities.details.subtitle', { city: facility.city }) : undefined}
        onBack={() => navigate('/transportation/facilities')}
        backLabel={t('facilities.details.backLabel')}
        sx={{ borderRadius: `${APP_SHAPE.radiusLg}px` }}
        actions={canEdit ? (
          <Button variant="contained" startIcon={<EditIcon />} onClick={openEdit}>
            {t('facilities.details.editFacility')}
          </Button>
        ) : undefined}
      />

      {error && !assignOpen && !formOpen && <Alert severity="error" onClose={clearError} sx={{ mb: 3 }}>{error}</Alert>}

      {isMirror ? (
        <Alert severity="info" icon={<LinkedIcon fontSize="inherit" />} sx={{ mb: 3 }}>
          {t('facilities.linked.readOnlyHint', { org: facility.linked_owner_org?.name ?? '' })}
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>{t('facilities.details.managerTitle')}</Typography>
            {facility.manager ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip icon={<ManagerIcon />} label={facility.manager.full_name || facility.manager.username || facility.manager.id} />
                {facility.manager.username && (
                  <Typography variant="caption" color="text.secondary">{facility.manager.username}</Typography>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{t('facilities.noManager')}</Typography>
            )}
          </Box>
          {canEdit && (
            <Stack direction="row" spacing={1.5}>
              <Button variant="outlined" startIcon={<ManagerIcon />} onClick={openAssign}>{t('facilities.details.changeManager')}</Button>
              {facility.manager && (
                <Button color="error" startIcon={<UnassignIcon />} onClick={removeManager}>{t('facilities.details.removeManager')}</Button>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      <Paper elevation={1} sx={{ borderRadius: `${APP_SHAPE.radiusLg}px`, overflow: 'hidden' }}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h6">{t('facilities.details.incomingOrdersTitle')}</Typography>
        </Box>
        <Divider />
        {incomingOrdersLoading ? (
          <Box sx={{ p: 2 }}><LoadingSkeleton variant="table" filters={false} columns={6} rows={4} /></Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('facilities.incomingOrders.columns.route')}</TableCell>
                    <TableCell>{t('facilities.incomingOrders.columns.truck')}</TableCell>
                    <TableCell>{t('facilities.incomingOrders.columns.driver')}</TableCell>
                    <TableCell>{t('facilities.incomingOrders.columns.material')}</TableCell>
                    <TableCell align="right">{t('facilities.incomingOrders.columns.amount')}</TableCell>
                    <TableCell>{t('facilities.incomingOrders.columns.status')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incomingOrders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>{order.route_name}</TableCell>
                      <TableCell>{order.truck_name}</TableCell>
                      <TableCell>{order.driver_name || '—'}</TableCell>
                      <TableCell>{order.material_name || order.waste_type}</TableCell>
                      <TableCell align="right">{order.amount}{order.amount_unit ? ` ${order.amount_unit}` : ''}</TableCell>
                      <TableCell><Chip size="small" label={order.status} /></TableCell>
                    </TableRow>
                  ))}
                  {incomingOrders.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ py: 6, border: 0, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('facilities.details.noIncomingOrders')}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ px: 2.5, pb: 2.5 }}>
              <PaginationControl page={ordersPage} limit={ORDERS_PAGE_SIZE} total={incomingOrdersTotal} onPageChange={setOrdersPage} />
            </Box>
          </>
        )}
      </Paper>

      {/* How a driver offloads a route's collected load at this facility. A mirror runs
          the real facility's flow, which only its owner can edit. */}
      {!isMirror ? (
      <Paper elevation={1} sx={{ mt: 3, p: { xs: 2, sm: 3 }, borderRadius: `${APP_SHAPE.radiusLg}px` }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          {t('collectionFlow.facilityTitle')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('collectionFlow.facilitySubtitle')}
        </Typography>
        <FlowBuilder
          organizationId={selectedOrganization.id}
          flowType="facility_offload"
          facilityId={facility.id}
        />
      </Paper>
      ) : null}

      <FacilityFormDialog
        open={formOpen}
        orgId={selectedOrganization.id}
        facility={facility}
        saving={isSaving}
        submitError={error}
        onClose={() => setFormOpen(false)}
        onSubmit={submitEdit}
      />

      <AssignFacilityManagerDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        loading={isSaving || managersLoading}
        facilityName={facility.name}
        availableManagers={managers}
        managerId={managerId}
        onManagerIdChange={setManagerId}
        onSubmit={() => void submitAssign()}
      />
    </Box>
  );
}
